// Added by Sejune Oh on 2020-06-15
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useState } from 'react'
import { createStyles, Theme, makeStyles, useTheme } from '@material-ui/core'

// Helper
import Intl from '@/helper/Intl'
import { TaskMonitoringElement } from '@/helper/define/taskDefine'
import Logger from '@/helper/Logger'

// api
import { reqCancelAllWaitingTasks } from '@/service/study/task.service'

// UI
import { DialogWrapper, DialogWrapperProps } from '@/components/common/v2/Dialog/DialogWrapper'
import { Button } from '@/components/worklist/Button'
import TaskMonitorignDetailTable from './TaskMonitoringDetailTable'
import { ConfirmDialog } from '@/components/common/v2/Dialog/ConfirmDialog'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface TaskMonitoringDetailProps extends Partial<DialogWrapperProps> {
  waitingTasks?: TaskMonitoringElement[]
  runningTasks?: TaskMonitoringElement[]
  btnTextOk?: string
  tableTitleWating?: string
  tableTitleRunning?: string
  proxyToken?: string | null
  /** WC용 Styles  */
  classes?: any
  isAdminLevel?: boolean
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const TaskMonitoringDetail = ({
  open,
  onCloseBtnClick,
  waitingTasks = [],
  runningTasks = [],
  title: titleParam,
  btnTextOk: btnTextOkParam,
  tableTitleWating: tableTitleWatingParam,
  tableTitleRunning: tableTitleRunningParam,
  proxyToken,
  isAdminLevel = false,
  ...rest
}: TaskMonitoringDetailProps) => {
  useTheme()
  const classes = useStyles()

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // i18n
  const title = titleParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.title' })
  const btnTextOk = btnTextOkParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.btn_ok' })
  const btnTextCancelAll = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.btn_cancel_all' })
  const tableTitleWating = tableTitleWatingParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.table_title_waiting' })
  const tableTitleRunning = tableTitleRunningParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.table_title_running' })
  const titleDialogCancelAll = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.dialog_title_cancel' })
  const msgConfirmCancelAll = Intl.getIntlText({ id: 'htmljs_react.worklist.taskmonitoringdetail.msg_confirm_cancel' })
  
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // function
  const getDialogWidth = () => {
    const innerWidth = window.innerWidth * 0.8
    return 1280 > innerWidth ? innerWidth : 1280
  }

  const handleCancelAll = () => {
    setConfirmDialogOpen(true)
  }

  const handleConfirmDialogCloseBtnClick = () => {
    setConfirmDialogOpen(false)
  }

  const handleConfirmDialogConfirmBtnClick = async () => {
    if (!proxyToken) return

    try {
      await reqCancelAllWaitingTasks(proxyToken)
    } catch (error) {
      Logger.errLog(`Can't cancel Process because: ${error}`)
    } finally {
      setConfirmDialogOpen(false)
    }
  }

  return (
    <DialogWrapper
      open={open!}
      title={title}
      onCloseBtnClick={onCloseBtnClick!}
      minwidth={getDialogWidth()}
      maxWidth={'lg'}
    >
      <div className={classes.contentWrapper}>
        <TaskMonitorignDetailTable
          tableTitle={tableTitleRunning}
          tasks={runningTasks}
          proxyToken={proxyToken}
          isAdminLevel={isAdminLevel}
          isRunning
        />
        <div className={classes.divider} />
        <TaskMonitorignDetailTable
          tableTitle={tableTitleWating}
          tasks={waitingTasks}
          proxyToken={proxyToken}
          isAdminLevel={isAdminLevel}
        />
      </div>
      <div className={classes.ctrWrapper}>
        <div className={isAdminLevel ? classes.ctrRowWithCancel : classes.ctrRow}>
          {isAdminLevel && (
            <Button type='cancel' onBtnClick={handleCancelAll}>
              {btnTextCancelAll}
            </Button>
          )}
          <Button disabled={false} onBtnClick={onCloseBtnClick!}>
            {btnTextOk}
          </Button>
        </div>
      </div>
      <ConfirmDialog
        title={titleDialogCancelAll}
        open={confirmDialogOpen}
        onCloseBtnClick={handleConfirmDialogCloseBtnClick}
        onConfirmBtnClick={handleConfirmDialogConfirmBtnClick}
        onCancelBtnClick={handleConfirmDialogCloseBtnClick}
        messages={msgConfirmCancelAll}
        hasCancelBtn
      />
    </DialogWrapper>
  )
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentWrapper: {
      padding: '8px',
      fontSize: '11px',
    },
    ctrWrapper: {
      display: 'flex',
      flexDirection: 'column',
      padding: '0 8px 8px 8px',
    },
    ctrRowWithCancel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      color: '#e6eaec',
    },
    ctrRow: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      color: '#e6eaec',
    },
    divider: {
      width: '100%',
      height: '1px',
      background: '#000',
      marginTop: theme.spacing(),
    },
  })
)

export default TaskMonitoringDetail

